import PermissionButton from "../../components/ui/PermissionButton";
import TableActions from '../../components/ui/TableActions';
import {
  cloneElement,
  isValidElement,
  useEffect,
  useMemo,
  useState,
} from "react";
import DashboardLayout from "../Layouts/DashboardLayout";
import { useAuth } from "../Context/AuthContext";
import DepartmentSelect from '../../components/ui/DepartmentSelect';

const API = "/api";
const emptyLine = () => ({
  inventory_item_id: null,
  item_name: "",
  item_type: "custom",
  qty: 1,
  rate: 0,
});
const money = (value) => `₹${Number(value || 0).toFixed(2)}`;

export default function SalesOrders() {
  const { user, branchId } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [search, setSearch] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [viewOrder, setViewOrder] = useState(null);
  const [showPayment, setShowPayment] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    customer_id: "",
    branch_id: "",
    order_type: "Sales",
    event_date: "",
    delivery_date: "",
    delivery_time: "",
    delivery_address: "",
    discount: 0,
    tax: 0,
    notes: "",
    items: [emptyLine()],
  });
  const [payment, setPayment] = useState({
    amount: "",
    payment_mode: "Cash",
    remarks: "",
  });
  const [itemSearch, setItemSearch] = useState("");
  const [itemTypeFilter, setItemTypeFilter] = useState("");

  const request = async (path, options = {}) => {
    const res = await fetch(`${API}${path}`, {
      credentials: "include",
      ...options,
    });
    const data = await res.json();
    if (!res.ok || data.status !== "success")
      throw new Error(data.message || "Request failed");
    return data;
  };

  const loadAll = async () => {
    try {
      const [orderData, customerData, branchData, inventoryData] =
        await Promise.all([
          request("/sales-orders/"),
          request("/customers/"),
          request("/branches"),
          request("/sales-orders/inventory-items"),
        ]);
      setOrders(orderData.data);
      setCustomers(customerData.data);
      setBranches(branchData.data);
      setInventory(inventoryData.data);
    } catch (error) {
      alert(error.message);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const subtotal = useMemo(
    () =>
      form.items.reduce(
        (total, line) => total + Number(line.qty || 0) * Number(line.rate || 0),
        0,
      ),
    [form.items],
  );
  const total = Math.max(
    0,
    subtotal - Number(form.discount || 0) + Number(form.tax || 0),
  );
  const selectableInventory = inventory.filter((item) => {
    const sameBranch =
      !form.branch_id || String(item.branch_id) === String(form.branch_id);
    const itemType = String(item.type || "raw").toLowerCase();
    const matchesType = !itemTypeFilter || itemType === itemTypeFilter;
    const matchesSearch = String(item.name || "")
      .toLowerCase()
      .includes(itemSearch.toLowerCase());
    return sameBranch && matchesType && matchesSearch;
  });
  const filtered = orders.filter((order) => {
    const haystack =
      `${order.order_no} ${order.customer} ${order.branch}`.toLowerCase();
    return (
      (!search || haystack.includes(search.toLowerCase())) &&
      (!branchFilter || String(order.branch_id) === branchFilter) &&
      (!statusFilter || order.order_status === statusFilter)
    );
  });

  const openNewOrder = () => {
    setForm({
      customer_id: "",
      branch_id: isSuperAdmin ? "" : String(branchId || user?.branch_id || ""),
      order_type: "Sales",
      event_date: "",
      delivery_date: "",
      delivery_time: "",
      delivery_address: "",
      discount: 0,
      tax: 0,
      notes: "",
      items: [emptyLine()],
    });
    setShowForm(true);
  };

  const updateLine = (index, field, value) =>
    setForm((current) => {
      const items = [...current.items];
      items[index] = { ...items[index], [field]: value };
      return { ...current, items };
    });

  const chooseInventoryItem = (index, id) => {
    const selected = inventory.find((item) => String(item.id) === String(id));
    if (!selected) return updateLine(index, "inventory_item_id", null);
    setForm((current) => {
      const items = [...current.items];
      items[index] = {
        ...items[index],
        inventory_item_id: selected.id,
        item_name: selected.name,
        item_type: selected.type || "raw",
      };
      return { ...current, items };
    });
  };

  const saveOrder = async () => {
    if (
      !form.customer_id ||
      !form.branch_id ||
      !form.department_id ||
      form.items.some((line) => !line.item_name.trim() || Number(line.qty) <= 0)
    ) {
      alert(
        "Select a customer, branch and sales department, then enter valid items and quantities.",
      );
      return;
    }
    setSaving(true);
    try {
      const result = await request("/sales-orders/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          customer_id: Number(form.customer_id),
          branch_id: Number(form.branch_id),
          department_id:Number(form.department_id),
          discount: Number(form.discount || 0),
          tax: Number(form.tax || 0),
          items: form.items.map((line) => ({
            ...line,
            inventory_item_id: line.inventory_item_id || null,
            qty: Number(line.qty),
            rate: Number(line.rate),
          })),
        }),
      });
      alert(`${result.order_no} created. ${result.message}`);
      setShowForm(false);
      loadAll();
    } catch (error) {
      alert(error.message);
    } finally {
      setSaving(false);
    }
  };

  const openOrder = async (id) => {
    try {
      setViewOrder((await request(`/sales-orders/${id}`)).order);
    } catch (error) {
      alert(error.message);
    }
  };
  const savePayment = async () => {
    if (!Number(payment.amount) || Number(payment.amount) <= 0) {
      alert("Enter a valid payment amount.");
      return;
    }
    try {
      const result = await request(`/sales-orders/${showPayment.id}/payment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...payment, amount: Number(payment.amount) }),
      });
      alert(`Payment saved. Invoice ${result.invoice_no} is ready.`);
      setShowPayment(null);
      setPayment({ amount: "", payment_mode: "Cash", remarks: "" });
      loadAll();
    } catch (error) {
      alert(error.message);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <header className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="text-3xl font-serif">Sales Orders</h1>
            <p className="mt-1 text-sm text-gray-400">
              Create orders now; the first payment creates the invoice.
            </p>
          </div>
          <PermissionButton permissionModule="sales_orders"
            onClick={openNewOrder}
            className="rounded-xl bg-amber-400 px-5 py-2.5 font-semibold text-black"
          >
            + New order
          </PermissionButton>
        </header>
        <section className="grid gap-3 rounded-2xl border border-white/10 bg-white/5 p-4 md:grid-cols-3">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search order, customer or branch"
            className="rounded-lg border border-white/10 bg-black/30 px-3 py-2"
          />
          {isSuperAdmin && (
            <select
              value={branchFilter}
              onChange={(e) => setBranchFilter(e.target.value)}
              className="rounded-lg border border-white/10 bg-black/30 px-3 py-2"
            >
              <option value="">All branches</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          )}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border border-white/10 bg-black/30 px-3 py-2"
          >
            <option value="">All order statuses</option>
            {[
              "Pending",
              "Confirmed",
              "Preparing",
              "Ready",
              "Delivered",
              "Completed",
              "Cancelled",
            ].map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </section>
        <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[#0b0b0f]">
          <table className="w-full min-w-[950px] text-sm">
            <thead className="bg-black/40 text-left text-amber-400">
              <tr>
                {[
                  "Order",
                  "Customer",
                  "Branch",
                  "Delivery",
                  "Total",
                  "Paid / Balance",
                  "Status",
                  "Actions",
                ].map((h) => (
                  <th key={h} className="p-4">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="8" className="p-10 text-center text-gray-500">
                    No sales orders found.
                  </td>
                </tr>
              ) : (
                filtered.map((o) => (
                  <tr
                    key={o.id}
                    className="border-t border-white/10 hover:bg-white/5"
                  >
                    <td className="p-4 font-semibold">
                      {o.order_no}
                      <div className="mt-1 text-xs text-gray-500">
                        {o.order_type}
                      </div>
                    </td>
                    <td className="p-4">{o.customer}</td>
                    <td className="p-4">{o.branch || "-"}</td>
                    <td className="p-4">{o.delivery_date || "-"}</td>
                    <td className="p-4">{money(o.grand_total)}</td>
                    <td className="p-4">
                      <div className="text-emerald-400">
                        {money(o.advance_paid)}
                      </div>
                      <div className="text-xs text-red-300">
                        Due {money(o.balance_amount)}
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="rounded-full bg-amber-400/15 px-2.5 py-1 text-xs text-amber-300">
                        {o.payment_status}
                      </span>
                    </td>
                    <td className="p-4"><TableActions>
                      <div className="flex gap-3">
                        <PermissionButton permissionModule="sales_orders"
                          onClick={() => openOrder(o.id)}
                          className="text-sky-400"
                        >
                          View
                        </PermissionButton>
                        <PermissionButton permissionModule="sales_orders"
                          disabled={Number(o.balance_amount) <= 0}
                          onClick={() => setShowPayment(o)}
                          className="text-emerald-400 disabled:text-gray-600"
                        >
                          Payment
                        </PermissionButton>
                      </div>
                    </TableActions></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {showForm && (
          <Modal
            title="Create sales order"
            onClose={() => setShowForm(false)}
            wide
          >
            <div className="grid gap-4 md:grid-cols-2">
              <DepartmentSelect value={form.department_id} onChange={value=>setForm({...form,department_id:value})} branchId={form.branch_id} module="sales" label="Sales department" />
              <Field label="Customer">
                <select
                  value={form.customer_id}
                  onChange={(e) =>
                    setForm({ ...form, customer_id: e.target.value })
                  }
                >
                  <option value="">Select customer</option>
                  {customers.map((c) => (
                    <option value={c.id} key={c.id}>
                      {c.name} {c.mobile ? `— ${c.mobile}` : ""}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Branch">
                <select
                  disabled={!isSuperAdmin}
                  value={form.branch_id}
                  onChange={(e) =>
                    setForm({ ...form, branch_id: e.target.value })
                  }
                >
                  <option value="">Select branch</option>
                  {branches.map((b) => (
                    <option value={b.id} key={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Order type">
                <input
                  value={form.order_type}
                  onChange={(e) =>
                    setForm({ ...form, order_type: e.target.value })
                  }
                />
              </Field>
              <Field label="Event date">
                <input
                  type="date"
                  value={form.event_date}
                  onChange={(e) =>
                    setForm({ ...form, event_date: e.target.value })
                  }
                />
              </Field>
              <Field label="Delivery date">
                <input
                  type="date"
                  value={form.delivery_date}
                  onChange={(e) =>
                    setForm({ ...form, delivery_date: e.target.value })
                  }
                />
              </Field>
              <Field label="Delivery time">
                <input
                  type="time"
                  value={form.delivery_time}
                  onChange={(e) =>
                    setForm({ ...form, delivery_time: e.target.value })
                  }
                />
              </Field>
            </div>
            <Field label="Delivery address">
              <input
                value={form.delivery_address}
                onChange={(e) =>
                  setForm({ ...form, delivery_address: e.target.value })
                }
              />
            </Field>
            <div className="mt-7 overflow-hidden rounded-2xl border border-white/10 bg-black/20">
              <div className="flex flex-col gap-3 border-b border-white/10 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="font-semibold text-white">Order items</h3>
                  <p className="mt-1 text-xs text-gray-400">
                    Choose finished goods or raw materials from this branch, or
                    enter a custom item.
                  </p>
                </div>
                <span className="rounded-full border border-amber-400/20 bg-amber-400/10 px-3 py-1 text-xs font-medium text-amber-300">
                  {form.items.length}{" "}
                  {form.items.length === 1 ? "item" : "items"}
                </span>
              </div>
              <div className="grid gap-3 border-b border-white/10 bg-black/20 p-4 md:grid-cols-3">
                <input
                  value={itemSearch}
                  onChange={(e) => setItemSearch(e.target.value)}
                  placeholder="Search inventory item..."
                  className="rounded-xl border border-white/10 bg-black/35 px-3.5 py-2.5 text-sm text-white outline-none focus:border-amber-400/70"
                />
                <select
                  value={itemTypeFilter}
                  onChange={(e) => setItemTypeFilter(e.target.value)}
                  className="rounded-xl border border-white/10 bg-black/35 px-3.5 py-2.5 text-sm text-white outline-none focus:border-amber-400/70"
                >
                  <option value="">All inventory types</option>
                  <option value="finished">Finished goods</option>
                  <option value="raw">Raw materials</option>
                </select>
                <div className="rounded-xl border border-white/10 bg-white/5 px-3.5 py-2.5 text-sm text-gray-300">
                  {selectableInventory.length} matching items
                </div>
              </div>
              <div className="space-y-3 p-4">
                {form.items.map((line, index) => {
                  const lineAmount =
                    Number(line.qty || 0) * Number(line.rate || 0);
                  const typeClass =
                    line.item_type === "finished"
                      ? "bg-emerald-400/10 text-emerald-300"
                      : line.item_type === "raw"
                        ? "bg-sky-400/10 text-sky-300"
                        : "bg-gray-400/10 text-gray-300";
                  return (
                    <div
                      key={index}
                      className="rounded-2xl border border-white/10 bg-[#121217] p-4"
                    >
                      <div className="mb-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-amber-400/15 text-xs font-bold text-amber-300">
                            {index + 1}
                          </span>
                          <span className="text-sm font-medium text-white">
                            Item {index + 1}
                          </span>
                          <span
                            className={`rounded-full px-2.5 py-1 text-xs font-medium capitalize ${typeClass}`}
                          >
                            {line.item_type || "custom"}
                          </span>
                        </div>
                        <PermissionButton permissionModule="sales_orders"
                          type="button"
                          disabled={form.items.length === 1}
                          onClick={() =>
                            setForm({
                              ...form,
                              items: form.items.filter((_, i) => i !== index),
                            })
                          }
                          className="text-xs font-medium text-red-300 disabled:text-gray-600"
                        >
                          Remove
                        </PermissionButton>
                      </div>
                      <div className="grid gap-4 lg:grid-cols-12">
                        <div className="lg:col-span-5">
                          <label className="mb-1.5 block text-xs text-gray-300">
                            Inventory item
                          </label>
                          <select
                            value={line.inventory_item_id || ""}
                            onChange={(e) =>
                              chooseInventoryItem(index, e.target.value)
                            }
                            className="w-full rounded-xl border border-white/10 bg-black/35 px-3.5 py-2.5 text-sm text-white outline-none focus:border-amber-400/70"
                          >
                            <option value="">
                              Custom item — type name manually
                            </option>
                            {selectableInventory.map((item) => (
                              <option key={item.id} value={item.id}>
                                {item.name} — {item.type || "raw"}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="lg:col-span-4">
                          <label className="mb-1.5 block text-xs text-gray-300">
                            Item name
                          </label>
                          <input
                            value={line.item_name}
                            placeholder="Example: Wedding sweets box"
                            onChange={(e) => {
                              updateLine(index, "item_name", e.target.value);
                              updateLine(index, "inventory_item_id", null);
                              updateLine(index, "item_type", "custom");
                            }}
                            className="w-full rounded-xl border border-white/10 bg-black/35 px-3.5 py-2.5 text-sm text-white outline-none focus:border-amber-400/70"
                          />
                        </div>
                        <div className="lg:col-span-3">
                          <label className="mb-1.5 block text-xs text-gray-300">
                            Item type
                          </label>
                          <select
                            value={line.item_type || "custom"}
                            disabled={Boolean(line.inventory_item_id)}
                            onChange={(e) =>
                              updateLine(index, "item_type", e.target.value)
                            }
                            className="w-full rounded-xl border border-white/10 bg-black/35 px-3.5 py-2.5 text-sm text-white outline-none disabled:opacity-60"
                          >
                            <option value="custom">Custom</option>
                            <option value="finished">Finished goods</option>
                            <option value="raw">Raw material</option>
                          </select>
                        </div>
                        <div className="lg:col-span-3">
                          <label className="mb-1.5 block text-xs text-gray-300">
                            Quantity
                          </label>
                          <input
                            min="0.001"
                            step="0.001"
                            type="number"
                            value={line.qty}
                            onChange={(e) =>
                              updateLine(index, "qty", e.target.value)
                            }
                            className="w-full rounded-xl border border-white/10 bg-black/35 px-3.5 py-2.5 text-sm text-white outline-none"
                          />
                        </div>
                        <div className="lg:col-span-3">
                          <label className="mb-1.5 block text-xs text-gray-300">
                            Rate
                          </label>
                          <input
                            min="0"
                            step="0.01"
                            type="number"
                            value={line.rate}
                            onChange={(e) =>
                              updateLine(index, "rate", e.target.value)
                            }
                            className="w-full rounded-xl border border-white/10 bg-black/35 px-3.5 py-2.5 text-sm text-white outline-none"
                          />
                        </div>
                        <div className="lg:col-span-3">
                          <label className="mb-1.5 block text-xs text-gray-300">
                            Line total
                          </label>
                          <div className="rounded-xl border border-amber-400/15 bg-amber-400/10 px-3.5 py-2.5 text-sm font-semibold text-amber-300">
                            {money(lineAmount)}
                          </div>
                        </div>
                        <div className="flex items-end lg:col-span-3">
                          <p
                            className={`pb-2 text-xs ${line.inventory_item_id ? "text-emerald-300" : "text-gray-500"}`}
                          >
                            {line.inventory_item_id
                              ? "✓ Linked to inventory"
                              : "Custom line item"}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <PermissionButton permissionModule="sales_orders"
                  type="button"
                  onClick={() =>
                    setForm({ ...form, items: [...form.items, emptyLine()] })
                  }
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-amber-400/35 bg-amber-400/5 px-4 py-3 text-sm font-semibold text-amber-300 hover:bg-amber-400/10"
                >
                  <span className="text-lg">+</span>Add another item
                </PermissionButton>
              </div>
            </div>
            <div className="mt-6 grid gap-4 md:grid-cols-4">
              <Field label="Discount">
                <input
                  min="0"
                  type="number"
                  value={form.discount}
                  onChange={(e) =>
                    setForm({ ...form, discount: e.target.value })
                  }
                />
              </Field>
              <Field label="GST amount">
                <input
                  min="0"
                  type="number"
                  value={form.tax}
                  onChange={(e) => setForm({ ...form, tax: e.target.value })}
                />
              </Field>
              <div className="rounded-lg bg-black/30 p-3">
                <div className="text-xs text-gray-400">Subtotal</div>
                <strong>{money(subtotal)}</strong>
              </div>
              <div className="rounded-lg bg-amber-400/10 p-3">
                <div className="text-xs text-amber-200">Order total</div>
                <strong className="text-amber-300">{money(total)}</strong>
              </div>
            </div>
            <Field label="Notes">
              <textarea
                rows="2"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </Field>
            <p className="mt-3 text-xs text-gray-400">
              No invoice is created here. The first payment will create a single
              linked invoice.
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <PermissionButton permissionModule="sales_orders"
                onClick={() => setShowForm(false)}
                className="rounded-xl border border-white/20 px-4 py-2"
              >
                Cancel
              </PermissionButton>
              <PermissionButton permissionModule="sales_orders"
                disabled={saving}
                onClick={saveOrder}
                className="rounded-xl bg-amber-400 px-5 py-2 font-semibold text-black disabled:opacity-60"
              >
                {saving ? "Saving…" : "Create order"}
              </PermissionButton>
            </div>
          </Modal>
        )}
        {viewOrder && (
          <Modal
            title={`${viewOrder.order_no} — order details`}
            onClose={() => setViewOrder(null)}
          >
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Info label="Customer" value={viewOrder.customer} />
              <Info
                label="Invoice"
                value={viewOrder.invoice_no || "Created on first payment"}
              />
              <Info label="Total" value={money(viewOrder.grand_total)} />
              <Info label="Balance" value={money(viewOrder.balance_amount)} />
            </div>
            <table className="mt-5 w-full text-sm">
              <thead className="text-left text-gray-400">
                <tr>
                  <th className="p-2">Item</th>
                  <th>Type</th>
                  <th>Qty</th>
                  <th>Rate</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {viewOrder.items.map((i, n) => (
                  <tr key={n} className="border-t border-white/10">
                    <td className="p-2">{i.item_name}</td>
                    <td>{i.item_type}</td>
                    <td>{i.qty}</td>
                    <td>{money(i.rate)}</td>
                    <td>{money(i.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Modal>
        )}
        {showPayment && (
          <Modal
            title={`Receive payment — ${showPayment.order_no}`}
            onClose={() => setShowPayment(null)}
          >
            <p className="mb-4 text-sm text-gray-400">
              Outstanding balance:{" "}
              <span className="font-semibold text-white">
                {money(showPayment.balance_amount)}
              </span>
            </p>
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Amount">
                <input
                  autoFocus
                  type="number"
                  min="0.01"
                  max={showPayment.balance_amount}
                  step="0.01"
                  value={payment.amount}
                  onChange={(e) =>
                    setPayment({ ...payment, amount: e.target.value })
                  }
                />
              </Field>
              <Field label="Payment mode">
                <select
                  value={payment.payment_mode}
                  onChange={(e) =>
                    setPayment({ ...payment, payment_mode: e.target.value })
                  }
                >
                  {["Cash", "UPI", "Card", "Bank Transfer"].map((m) => (
                    <option key={m}>{m}</option>
                  ))}
                </select>
              </Field>
            </div>
            <Field label="Remarks">
              <textarea
                rows="2"
                value={payment.remarks}
                onChange={(e) =>
                  setPayment({ ...payment, remarks: e.target.value })
                }
              />
            </Field>
            <div className="mt-5 flex justify-end gap-3">
              <PermissionButton permissionModule="sales_orders"
                onClick={() => setShowPayment(null)}
                className="rounded-xl border border-white/20 px-4 py-2"
              >
                Cancel
              </PermissionButton>
              <PermissionButton permissionModule="sales_orders"
                onClick={savePayment}
                className="rounded-xl bg-emerald-400 px-5 py-2 font-semibold text-black"
              >
                Save payment & create invoice
              </PermissionButton>
            </div>
          </Modal>
        )}
      </div>
    </DashboardLayout>
  );
}

function Field({ label, children }) {
  const className =
    "w-full rounded-xl border border-white/10 bg-black/35 px-3.5 py-2.5 text-sm text-white outline-none transition placeholder:text-gray-600 focus:border-amber-400/70 focus:ring-2 focus:ring-amber-400/15 disabled:cursor-not-allowed disabled:opacity-60";
  const control = isValidElement(children)
    ? cloneElement(children, {
        className: `${className} ${children.props.className || ""}`,
      })
    : children;
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-medium tracking-wide text-gray-300">
        {label}
      </span>
      {control}
    </label>
  );
}
function Info({ label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/25 p-4">
      <div className="text-xs font-medium uppercase tracking-wider text-gray-500">
        {label}
      </div>
      <div className="mt-1.5 break-words text-sm font-semibold text-white">
        {value}
      </div>
    </div>
  );
}
function Modal({ title, onClose, children, wide = false }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/80 px-3 py-6 backdrop-blur-sm sm:px-6"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className={`w-full overflow-hidden rounded-3xl border border-white/10 bg-[#101014] shadow-2xl shadow-black/60 ${wide ? "max-w-6xl" : "max-w-3xl"}`}
      >
        <div className="flex items-start justify-between border-b border-white/10 bg-transparent px-5 py-5 sm:px-7">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-400">
              Jaggi Sweets
            </p>
            <h2 className="mt-1 text-xl font-semibold text-white sm:text-2xl">
              {title}
            </h2>
          </div>
          <PermissionButton permissionModule="sales_orders"
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-black/30 text-xl text-gray-400 transition hover:border-red-400/50 hover:bg-red-500/10 hover:text-red-300"
            aria-label="Close modal"
          >
            ×
          </PermissionButton>
        </div>
        <div className="max-h-[calc(92vh-100px)] overflow-y-auto px-5 py-6 sm:px-7">
          {children}
        </div>
      </div>
    </div>
  );
}
